import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal, LocateFixed, List, Map as MapIcon } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SpotCard from "../components/SpotCard";
import MapView from "../components/MapView";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { EmptyState, ErrorState, CardSkeleton } from "../components/States";
import { api } from "../lib/api";
import { getRegion, regionCenter, setRegion as saveRegion, countryName } from "../lib/region";
import RegionChip from "../components/RegionChip";
import CountryPicker from "../components/CountryPicker";
import { setPageTitle } from "../lib/format";
import { fadeUp, revealOnce, staggerParent } from "../lib/motion";
import { toast } from "sonner";

const TYPES = ["All", "Underground", "Covered", "Open", "Rooftop", "Private"];
const SORTS = [["nearest", "Nearest"], ["price_asc", "Price · low to high"], ["price_desc", "Price · high to low"], ["rating", "Top rated"]];
const DEFAULT_CENTER = { lat: 43.6532, lng: -79.3832 };

export default function FindParking() {
  const [params] = useSearchParams();
  const [spots, setSpots] = useState(null);
  const [error, setError] = useState(null);
  const paramLat = parseFloat(params.get("lat"));
  const paramLng = parseFloat(params.get("lng"));
  const initialCenter = Number.isFinite(paramLat) && Number.isFinite(paramLng) ? { lat: paramLat, lng: paramLng } : DEFAULT_CENTER;
  const [center, setCenter] = useState(initialCenter);
  const [placeLabel, setPlaceLabel] = useState(params.get("q") || "Toronto, ON");
  const [query, setQuery] = useState(params.get("q") || "");
  const [type, setType] = useState(params.get("type") || "All");
  const [sort, setSort] = useState("nearest");
  const [maxPrice, setMaxPrice] = useState("");
  const [startDate, setStartDate] = useState(params.get("start_date") || "");
  const [startTime, setStartTime] = useState(params.get("start_time") || "");
  const [endDate, setEndDate] = useState(params.get("end_date") || "");
  const [endTime, setEndTime] = useState(params.get("end_time") || "");
  const [showFilters, setShowFilters] = useState(!!(params.get("start_date") || params.get("start_time") || params.get("end_date") || params.get("end_time")));
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [mobileView, setMobileView] = useState("list");
  const [region, setRegion] = useState(undefined); // undefined = detecting

  useEffect(() => setPageTitle("Find Parking"), []);

  const load = useCallback(async (c = center) => {
    setError(null);
    try {
      const r = await api.nearby(c.lat, c.lng, 25);
      setSpots(r);
    } catch (e) {
      setError(e.message);
      setSpots([]);
    }
  }, [center]);

  // Region-first default: explicit URL coords win; otherwise center on the
  // visitor's detected/deliberately-chosen country (server enforces the filter).
  useEffect(() => {
    if (Number.isFinite(paramLat) && Number.isFinite(paramLng)) { getRegion().then(setRegion); load(initialCenter); return; }
    getRegion().then((r) => {
      setRegion(r);
      const c = regionCenter(r?.country);
      setCenter(c);
      setPlaceLabel(c.label);
      load(c);
    });
  }, []); // eslint-disable-line

  const onRegionChange = (r) => {
    setRegion(r);
    const c = regionCenter(r.country);
    setCenter(c); setPlaceLabel(c.label); setQuery("");
    load(c);
  };

  const onSelectPlace = (s) => {
    setQuery(s.main_text || s.description);
    if (s.lat != null && s.lng != null) {
      setPlaceLabel(s.main_text || s.description);
      const c = { lat: s.lat, lng: s.lng };
      setCenter(c);
      load(c);
    } else {
      toast.info("Resolving location…");
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation is not supported by this browser"); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(c);
        load(c);
        try {
          const r = await api.geoReverse(c.lat, c.lng);
          if (r.formatted_address) { setPlaceLabel(r.formatted_address); setQuery(r.formatted_address); }
          else setPlaceLabel("Your location");
        } catch { setPlaceLabel("Your location"); }
      },
      () => toast.error("Location permission denied — showing Toronto area"),
      { timeout: 8000 }
    );
  };

  // Requested parking window — the homepage search passes start/end date+time.
  const range = useMemo(() => {
    if (!startDate || !startTime || !endDate || !endTime) return null;
    const s = new Date(`${startDate}T${startTime}:00`), e = new Date(`${endDate}T${endTime}:00`);
    if (isNaN(s) || isNaN(e) || e <= s) return { invalid: true };
    return { s, e };
  }, [startDate, startTime, endDate, endTime]);

  const filtered = useMemo(() => {
    if (!spots) return [];
    let out = [...spots];
    if (type !== "All") out = out.filter((s) => s.space_type === type);
    if (maxPrice) out = out.filter((s) => s.price_per_hour <= Number(maxPrice));
    // Availability windows are real listing data: only show spaces whose
    // available days/hours cover the requested start→end window.
    if (range && !range.invalid) {
      const sh = range.s.getHours() + range.s.getMinutes() / 60;
      const eh = range.e.getHours() + range.e.getMinutes() / 60;
      out = out.filter((sp) => {
        if (sp.available_days?.length && (!sp.available_days.includes(range.s.getDay()) || !sp.available_days.includes(range.e.getDay()))) return false;
        if (sp.available_start_hour != null && sh < sp.available_start_hour) return false;
        if (sp.available_end_hour != null && eh > sp.available_end_hour) return false;
        return true;
      });
    }
    out.sort((a, b) => {
      if (sort === "price_asc") return a.price_per_hour - b.price_per_hour;
      if (sort === "price_desc") return b.price_per_hour - a.price_per_hour;
      if (sort === "rating") return (b.rating ?? -1) - (a.rating ?? -1);
      return (a.distance_km ?? 9e9) - (b.distance_km ?? 9e9);
    });
    return out;
  }, [spots, type, sort, maxPrice, range]);

  const onMarkerSelect = (spotId) => {
    setSelectedId(spotId);
    setMobileView("list");
    setTimeout(() => {
      document.querySelector(`[data-testid="spot-card-${spotId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const hasResults = !error && spots && filtered.length > 0;

  return (
    <div className="min-h-screen flex flex-col" data-testid="find-parking-page">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <motion.div variants={staggerParent(0.06)} initial="hidden" animate="show">
          <motion.p variants={fadeUp} className="section-label">Find parking</motion.p>
          <motion.h1 variants={fadeUp} className="type-display mt-4">Where are you headed?</motion.h1>
          <motion.p variants={fadeUp} className="mt-3 text-sm text-zinc-500 flex flex-wrap items-center gap-3">
            <span>Showing spaces near <span className="text-zinc-200 font-medium" data-testid="search-location-label">{placeLabel}</span></span>
            <RegionChip region={region} onChange={onRegionChange} testId="find-region-chip" />
          </motion.p>
        </motion.div>

        <div className="mt-9 grid lg:grid-cols-[1fr_auto] gap-3">
          <AddressAutocomplete value={query} onChange={setQuery} onSelect={onSelectPlace} testId="search-address-input" />
          <div className="flex gap-3">
            <button onClick={useMyLocation} data-testid="use-my-location-btn" className="btn-secondary !px-4 !h-12" aria-label="Use my location">
              <LocateFixed className="w-4 h-4" /><span className="hidden sm:inline">Near me</span>
            </button>
            <button onClick={() => setShowFilters((f) => !f)} data-testid="toggle-filters-btn" className="btn-secondary !px-4 !h-12" aria-expanded={showFilters}>
              <SlidersHorizontal className="w-4 h-4" /><span className="hidden sm:inline">Filters</span>
            </button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label="Space type">
          {TYPES.map((t) => (
            <button key={t} role="tab" aria-selected={type === t} data-testid={`filter-type-${t.toLowerCase()}`}
                    onClick={() => setType(t)}
                    className={`px-4 py-2 rounded-lg text-xs font-semibold border transition-all duration-200 ${type === t ? "bg-[#E50914] border-[#E50914] text-white" : "bg-transparent border-[#27272A] text-zinc-400 hover:border-[#3F3F46] hover:text-zinc-200"}`}>
              {t}
            </button>
          ))}
        </div>

        {showFilters && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.28 }}
                      className="mt-5 surface-flat p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-hidden" data-testid="filters-panel">
            <div>
              <label htmlFor="f-start-date" className="label-dark">Start date</label>
              <input id="f-start-date" data-testid="filter-start-date" type="date" value={startDate} min={new Date().toLocaleDateString("sv-SE")} onChange={(e) => setStartDate(e.target.value)} className="input-dark" />
            </div>
            <div>
              <label htmlFor="f-start" className="label-dark">Start time</label>
              <input id="f-start" data-testid="filter-start-time" type="time" step="900" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="input-dark" />
            </div>
            <div>
              <label htmlFor="f-end-date" className="label-dark">End date</label>
              <input id="f-end-date" data-testid="filter-end-date" type="date" value={endDate} min={startDate || new Date().toLocaleDateString("sv-SE")} onChange={(e) => setEndDate(e.target.value)} className="input-dark" />
            </div>
            <div>
              <label htmlFor="f-end" className="label-dark">End time</label>
              <input id="f-end" data-testid="filter-end-time" type="time" step="900" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="input-dark" />
            </div>
            <div>
              <label htmlFor="f-price" className="label-dark">Max $/hr</label>
              <input id="f-price" data-testid="filter-max-price" type="number" min="0" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="input-dark" placeholder="Any" />
            </div>
            <div>
              <label htmlFor="f-sort" className="label-dark">Sort by</label>
              <select id="f-sort" data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="input-dark">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {range?.invalid && (
              <p role="alert" data-testid="filters-time-error" className="sm:col-span-2 lg:col-span-3 -mt-1 text-xs font-medium text-[#FF6B6B]">
                End must be later than start — results are not time-filtered until the range is valid.
              </p>
            )}
          </motion.div>
        )}

        {/* Mobile LIST / MAP switch */}
        {hasResults && (
          <div className="lg:hidden mt-8 grid grid-cols-2 gap-1 p-1 surface-flat" role="tablist" aria-label="View">
            {[["list", "List", List], ["map", "Map", MapIcon]].map(([v, l, Icon]) => (
              <button key={v} role="tab" aria-selected={mobileView === v} data-testid={`mobile-view-${v}`} onClick={() => setMobileView(v)}
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-semibold transition-colors ${mobileView === v ? "bg-[#E50914] text-white" : "text-zinc-400"}`}>
                <Icon className="w-4 h-4" />{l}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 lg:mt-12 lg:grid lg:grid-cols-[1fr_minmax(380px,42%)] lg:gap-8">
          <div className={hasResults && mobileView === "map" ? "hidden lg:block" : ""}>
            {error ? (
              <ErrorState body={error} onRetry={() => load()} />
            ) : spots === null ? (
              <CardSkeleton count={8} />
            ) : filtered.length === 0 ? (
              region && !region.country ? (
                <div className="surface-flat p-10 max-w-md" data-testid="region-fallback">
                  <p className="type-h3">Choose your country to see parking near you.</p>
                  <p className="mt-2 text-sm text-zinc-500">We couldn't determine your region automatically. Pick your country — no precise location needed.</p>
                  <div className="mt-5">
                    <CountryPicker value="" onChange={async (cc) => onRegionChange(await saveRegion(cc))} testId="region-fallback-picker" />
                  </div>
                </div>
              ) : (
                <EmptyState title={spots && spots.length === 0 && region?.country ? `No parking spaces available in ${countryName(region.country)}` : "No parking found here yet"}
                  body={spots && spots.length === 0 && region?.country ? "New hosts list spaces every day — or choose another region above." : "Nothing matches this area or your filters right now. Widen the search or clear a filter — new hosts list spaces every day."} />
              )
            ) : (
              <>
                <p className="meta-label mb-5" data-testid="results-count">{filtered.length} space{filtered.length === 1 ? "" : "s"} available</p>
                <motion.div variants={staggerParent(0.05)} {...revealOnce}
                            className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="spots-grid">
                  {filtered.map((s) => (
                    <motion.div key={s.spot_id} variants={fadeUp}
                                onMouseEnter={() => setHoveredId(s.spot_id)} onMouseLeave={() => setHoveredId(null)}
                                className={selectedId === s.spot_id ? "rounded-2xl ring-1 ring-[#E50914] transition-shadow" : "transition-shadow"}>
                      <SpotCard spot={s} />
                    </motion.div>
                  ))}
                </motion.div>
              </>
            )}
          </div>
          {hasResults && (
            <div className={`${mobileView === "map" ? "block" : "hidden"} lg:block mt-6 lg:mt-0`}>
              <div className="lg:sticky lg:top-28 h-[60vh] lg:h-[calc(100vh-9rem)]">
                <p className="meta-label mb-3 hidden lg:block">Map</p>
                <MapView spots={filtered} selectedId={selectedId} hoveredId={hoveredId} onSelect={onMarkerSelect} className="h-full" />
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
